/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for managing labs via the Flask API.
 */
angular.module('nsadmin').factory('labService', ['$injector',
    function labService($injector) {

        var flaskApiService = $injector.get('flaskApiService');

        var service = {};

        /**
         * Gets all labs from the Flask API.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of lab objects.
         */
        service.getAllLabs = function getAllLabs() {
            return flaskApiService.get('/labs');
        };

        /**
         * Creates a new lab.
         *
         * @param {Object} labData
         *     The lab data including lab_id, name, and description.
         *
         * @returns {Promise}
         *     A promise that resolves with the created lab object.
         */
        service.createLab = function createLab(labData) {
            return flaskApiService.post('/labs', labData);
        };

        /**
         * Updates an existing lab.
         *
         * @param {String} labId
         *     The ID of the lab to update.
         *
         * @param {Object} labData
         *     The updated lab data.
         *
         * @returns {Promise}
         *     A promise that resolves with the updated lab object.
         */
        service.updateLab = function updateLab(labId, labData) {
            return flaskApiService.put('/labs/' + labId, labData);
        };

        /**
         * Deletes a lab.
         *
         * @param {String} labId
         *     The ID of the lab to delete.
         *
         * @returns {Promise}
         *     A promise that resolves when the lab is deleted.
         */
        service.deleteLab = function deleteLab(labId) {
            return flaskApiService.delete('/labs/' + labId);
        };

        /**
         * Gets all devices assigned to a specific lab.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @returns {Promise}
         *     A promise that resolves with an array of device assignments.
         */
        service.getLabDevices = function getLabDevices(labId) {
            return flaskApiService.get('/labs/' + labId + '/devices');
        };

        /**
         * Gets all lab device requirements (across all labs).
         *
         * @returns {Promise}
         *     A promise that resolves with an array of lab requirements.
         */
        service.getLabRequirements = function getLabRequirements() {
            return flaskApiService.get('/lab-requirements');
        };

        /**
         * Assigns a device to a lab.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @param {Object} deviceData
         *     The device data including device_slot and is_shared flag.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is assigned.
         */
        service.addDeviceToLab = function addDeviceToLab(labId, deviceData) {
            return flaskApiService.post('/labs/' + labId + '/devices', deviceData);
        };

        /**
         * Removes a device from a lab.
         *
         * @param {String} labId
         *     The ID of the lab.
         *
         * @param {String} deviceSlot
         *     The device slot identifier.
         *
         * @param {Boolean} isShared
         *     Whether the device is a shared device.
         *
         * @returns {Promise}
         *     A promise that resolves when the device is removed.
         */
        service.removeDeviceFromLab = function removeDeviceFromLab(labId, deviceSlot, isShared) {
            return flaskApiService.delete('/labs/' + labId + '/devices/' + deviceSlot +
                '?is_shared=' + (isShared ? 'true' : 'false'));
        };

        /**
         * Gets available device slots (both pod devices and shared).
         *
         * @returns {Promise}
         *     A promise that resolves with device slot information.
         */
        service.getAvailableDeviceSlots = function getAvailableDeviceSlots() {
            return flaskApiService.get('/device-slots');
        };

        /**
         * Gets the lab allocations summary for the dashboard.
         *
         * @returns {Promise}
         *     A promise that resolves with lab allocation statistics.
         */
        service.getLabAllocations = function getLabAllocations() {
            return flaskApiService.get('/lab-allocations');
        };

        return service;
    }
]);
